
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Share2, FileText, FileEdit, Calendar } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import type { Project } from "@/types/project";
import { ExportPDFDialog } from "./ExportPDFDialog";
import { useAuth } from "@/components/auth/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProjectHeaderSectionProps {
  project: Project;
  isPublicView?: boolean;
  canEdit?: boolean;
  onEditClick?: () => void;
  onShareClick?: () => void;
}

export function ProjectHeaderSection({
  project,
  isPublicView = false,
  canEdit = false,
  onEditClick,
  onShareClick,
}: ProjectHeaderSectionProps) {
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const { user } = useAuth();

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      // Ensure we're working with just the date part
      const datePart = dateStr.split('T')[0];
      const date = parseISO(datePart);
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateStr;
    }
  };

  return (
    <div className="flex flex-col gap-6 py-6 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm shadow-lg p-6 mb-8 animate-fade-in">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {isPublicView && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-blue-400 bg-blue-400/10 px-2 py-1 rounded-full border border-blue-400/20">
                SHARED PROJECT
              </span>
            </div>
          )}
          <h1 className="text-3xl font-bold text-white font-brakle break-words bg-gradient-to-br from-white via-white/90 to-white/70 bg-clip-text text-transparent">
            {project.name}
          </h1>
          {project.description && (
            <p className="mt-2 text-sm text-white/80">{project.description}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            {project.prep_date && (
              <Badge variant="secondary" className="bg-[#4B7BF5] hover:bg-[#4B7BF5]/90 text-white px-3 py-1.5">
                <Calendar className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                <span className="truncate">Prep: {formatDate(project.prep_date)}</span>
              </Badge>
            )}
            {project.pre_light_date && (
              <Badge variant="secondary" className="bg-[#9B87F5] hover:bg-[#9B87F5]/90 text-white px-3 py-1.5">
                <Calendar className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                <span className="truncate">Pre-Light: {formatDate(project.pre_light_date)}</span>
              </Badge>
            )}
            {project.shoot_dates && project.shoot_dates.length > 0 && (
              <Badge variant="secondary" className="bg-[#4CAF50] hover:bg-[#4CAF50]/90 text-white px-3 py-1.5">
                <Calendar className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                <span className="truncate">
                  Shoot: {project.shoot_dates.map((date, index) => (
                    <span key={date}>
                      {formatDate(date)}
                      {index < project.shoot_dates!.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </span>
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto mt-4 lg:mt-0">
          <Button
            variant="outline"
            size="sm"
            className="text-white border-white/20 hover:bg-white/10 hover:text-white justify-center"
            onClick={() => setShowPDFDialog(true)}
          >
            <FileText className="h-4 w-4 mr-2 shrink-0" />
            Export PDF
          </Button>
          {!isPublicView && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white/20 hover:bg-white/10 hover:text-white justify-center"
                onClick={onShareClick}
              >
                <Share2 className="h-4 w-4 mr-2 shrink-0" />
                Share
              </Button>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-white/20 hover:bg-white/10 hover:text-white justify-center"
                  onClick={onEditClick}
                >
                  <FileEdit className="h-4 w-4 mr-2 shrink-0" />
                  Edit
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <ExportPDFDialog
        project={project}
        isOpen={showPDFDialog}
        onOpenChange={setShowPDFDialog}
      />
    </div>
  );
}
